import React, { useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import styled from 'styled-components';
import { TextField, Button, Text, SubHeading, FlexRow, FlexColumn, ComboBox, Checkbox } from '@forecast-it/design-system';
import MissionControlHeader from './MissionControlHeader';
import { hideLoader, showLoader } from '../../global_loader';
import CreateSageCompanyMutation from '../../../mutations/mission_control/ts/CreateSageCompanyMutation';
import { createToast } from '../../../forecast-app/shared/components/toasts/toast';
import { MODAL_TYPE, showModal } from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import { CommitMutation } from '../../../mutations/ts/CommitMutation';
const Content = styled.div `
	display: flex;
	margin: 24px;
	width: 500px;
`;
function getSageCustomerItems(viewer) {
    var _a;
    if (!((_a = viewer === null || viewer === void 0 ? void 0 : viewer.company) === null || _a === void 0 ? void 0 : _a.unlinkedSageCustomers)) {
        return [];
    }
    const items = [];
    viewer.company.unlinkedSageCustomers.forEach(customer => {
        if (customer) {
            items.push({ id: customer.id, name: customer.name || 'No name' });
        }
    });
    return items;
}
function SageSignupControlPage({ viewer }) {
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [emailValid, setEmailValid] = useState(true);
    const [sageCompanyId, setSageCompanyId] = useState(null);
    const [projectFinancialsAccess, setProjectFinancialsAccess] = useState(false);
    const [revenueAccess, setRevenueAccess] = useState(false);
    const [financeMenuAccess, setFinanceMenuAccess] = useState(false);
    const [loading, setLoading] = useState(false);
    function validateEmail(email) {
        const valid = email.trim() === '' || /(.+)@(.+)\.(.+)/.test(email.trim());
        setEmailValid(valid);
        setEmail(email.trim());
    }
    function submit() {
        if (loading) {
            return;
        }
        showLoader();
        setLoading(true);
        const domain = email.split('@')[1].split('.')[0];
        const accountName = domain.replace(/^./, domain[0].toUpperCase());
        CommitMutation(CreateSageCompanyMutation, {
            email,
            companyName: companyName || accountName[0].toUpperCase() + accountName.slice(1).toLowerCase(),
            unlinkedSageCustomerID: sageCompanyId,
            projectFinancialsAccess,
            revenueAccess,
            financeMenuAccess,
        }, response => {
            var _a;
            hideLoader();
            setLoading(false);
            // Check response
            let errorMessage;
            const resultCode = (_a = response.CreateSageCompany) === null || _a === void 0 ? void 0 : _a.resultCode;
            switch (resultCode) {
                case '0':
                    // Success
                    createToast({ duration: 5000, message: 'Company Successfully created!' });
                    break;
                case '1':
                    // Email is missing...
                    errorMessage = `Create company failed with resultCode: ${resultCode}. Could not find email`;
                    break;
                case '2':
                    // Email in use
                    errorMessage = `Create company failed with resultCode: ${resultCode}. Email is already in use.`;
                    break;
                default:
                    // Other error. Contact a developer...
                    errorMessage = `Create company failed with resultCode: ${resultCode}. Contact a developer to find out what it means...`;
            }
            if (errorMessage) {
                showModal({
                    type: MODAL_TYPE.WARNING,
                    message: 'Error creating company',
                    warningInformation: [errorMessage],
                    buttons: [
                        {
                            text: 'Ok',
                        },
                    ],
                });
            }
        });
    }
    return (React.createElement("div", null,
        React.createElement(MissionControlHeader, { viewer: viewer }),
        React.createElement(Content, null,
            React.createElement(FlexColumn, null,
                React.createElement(SubHeading, null, "Sage PSA Signup"),
                React.createElement(Text, null, "Here you can create a new Forecast company with the Sage module enabled."),
                React.createElement(Text, null, "Sage admin will receive an email with further instructions to complete the signup."),
                React.createElement(ComboBox, { label: "Sage Company", value: sageCompanyId, items: getSageCustomerItems(viewer), onChange: setSageCompanyId }, (sageCompany) => (React.createElement(ComboBox.Item, { textValue: sageCompany.name },
                    React.createElement(Text, null, sageCompany.name)))),
                React.createElement(TextField, { label: "Company Name", value: companyName, onValueChange: val => {
                        setCompanyName(val);
                    }, placeholder: "Write company name here..." }),
                React.createElement(FlexRow, { alignItems: "end" },
                    React.createElement(TextField, { label: "Email", value: email, onChange: e => {
                            validateEmail(e.target.value);
                        }, placeholder: "Write Sage admin email here..." })),
                React.createElement(SubHeading, null, "Financial Configuration Options"),
                React.createElement(Text, { color: Text.Color.auxiliary }, "These can be changed later from the Modules control panel page."),
                React.createElement(Checkbox, { labelText: 'Project Financials on', checked: projectFinancialsAccess, onChange: () => setProjectFinancialsAccess(!projectFinancialsAccess) }),
                React.createElement(Checkbox, { labelText: 'Revenue on', checked: revenueAccess, onChange: () => setRevenueAccess(!revenueAccess), disabled: !projectFinancialsAccess }),
                React.createElement(Checkbox, { labelText: 'Finance menu on', checked: financeMenuAccess, onChange: () => setFinanceMenuAccess(!financeMenuAccess), disabled: !projectFinancialsAccess }),
                React.createElement(Button, { disabled: !email || !emailValid || loading || !sageCompanyId, onClick: () => {
                        submit();
                    }, noMargin: true }, "Signup")))));
}
const sageSignupQuery = graphql `
	query SageSignupControlPage_Query {
		viewer {
			actualPersonId
			component(name: "sage_signup")
			...SageSignupControlPage_viewer
		}
	}
`;
export { sageSignupQuery };
export default createFragmentContainer(SageSignupControlPage, {
    viewer: graphql `
		fragment SageSignupControlPage_viewer on Viewer {
			id
			...MissionControlHeader_viewer
			company {
				id
				unlinkedSageCustomers {
					id
					name
				}
			}
		}
	`,
});
